import React from 'react';
import type { FmTableColumn, FmTableProps } from '../../types/fm-table';
import { cx } from './FmTable.utils';
import { TableCell, TableErrorState } from './FmTable.render';
import { TableDataRow } from './FmTable.datarow';
import { TableShell } from './FmTable.shell';
import {
  FM_TABLE_LABELLED_CLASS,
  FM_TABLE_NARROW_CLASS,
  FM_TABLE_PINNED_CLASS,
  useFmTableCore,
  useTableFit,
} from './FmTable.hooks';
import { useLanguage } from '@/i18n';
import './ui.css';
import './FmTable.css';

interface ResolvedTableConfig {
  loading: boolean;
  showExpandColumn: boolean;
  totalColumnCount: number;
  canSort: boolean;
  effectiveDensity: 'compact' | 'comfortable';
  usesLabelledCells: boolean;
  emptyCellLabel: string | undefined;
  summaryContent: React.ReactNode;
  tableClass: string;
  dataTestId: string;
  loadingTestId: string;
  errorShellTestId: string;
}

interface TableLayoutFlags {
  loading: boolean;
  showExpandColumn: boolean;
  totalColumnCount: number;
  canSort: boolean;
  effectiveDensity: 'compact' | 'comfortable';
  usesLabelledCells: boolean;
  emptyCellLabel: string | undefined;
  summaryContent: React.ReactNode;
  dataTestId: string;
}

function resolveTableLayout<T extends object>(props: FmTableProps<T>): TableLayoutFlags {
  const effectiveDensity = props.density ?? (props.dense ? 'compact' : 'comfortable');
  const usesLabelledCells = props.mobileLayout === 'labelled-cells' || props.mobileLayout === 'cards';
  const showExpandColumn = Boolean(props.expandable) && props.expandable?.showExpandColumn !== false;
  const summary = props.summary;
  return {
    loading: props.loading ?? false,
    showExpandColumn,
    totalColumnCount: props.columns.length + (showExpandColumn ? 1 : 0),
    canSort: !props.groupBy,
    effectiveDensity,
    usesLabelledCells,
    emptyCellLabel: props.emptyCellLabel,
    summaryContent: typeof summary === 'function' ? summary(props.rows) : summary,
    dataTestId: props['data-testid'] ?? 'fm-table',
  };
}

function buildTableClass<T extends object>(
  props: FmTableProps<T>,
  effectiveDensity: 'compact' | 'comfortable',
  usesLabelledCells: boolean,
): string {
  const layout = props.layout ?? 'standard';
  return cx(
    'fm-table',
    props.stickyHead && 'fm-table--sticky-head',
    props.nonInteractiveHead && 'fm-table--noninteractive-head',
    effectiveDensity === 'compact' && 'fm-table--dense',
    `fm-table--density-${effectiveDensity}`,
    layout !== 'standard' && `fm-table--layout-${layout}`,
    usesLabelledCells && 'fm-table--mobile-labelled-cells',
    props.className,
  );
}

/** Resolve every prop-derived value once so the component body stays simple. */
function resolveTableConfig<T extends object>(props: FmTableProps<T>): ResolvedTableConfig {
  const layout = resolveTableLayout(props);
  return {
    ...layout,
    tableClass: buildTableClass(props, layout.effectiveDensity, layout.usesLabelledCells),
    loadingTestId: layout.dataTestId === 'fm-table' ? 'fm-table-loading' : layout.dataTestId,
    errorShellTestId: layout.dataTestId === 'fm-table' ? 'fm-table-error-shell' : layout.dataTestId,
  };
}

/** FmTable - Lehti AI DS native semantic data table. */
function FmTable<T extends object>(props: FmTableProps<T>): React.ReactElement {
  const { t } = useLanguage();
  const {
    columns,
    rowKey,
    caption,
    error,
    emptyText,
    groupHeader,
    groupCountLabel,
    expandable,
    footerRow,
    footerRowClassName,
    footerRowTestId,
    rowClassName,
    rowTestId,
    onRowSelect,
    onRowDoubleClick,
    rowAriaLabel,
    rowCurrent,
    rowDataAttributes,
    rowProps,
    headerRowTestId,
    headTestId,
    locale,
    style,
  } = props;

  const config = resolveTableConfig(props);
  const a11yLabels = t.common.ui.table;
  const {
    loading,
    showExpandColumn,
    totalColumnCount,
    canSort,
    usesLabelledCells,
    emptyCellLabel,
    summaryContent,
    tableClass,
    dataTestId,
    loadingTestId,
    errorShellTestId,
  } = config;

  const reactId = React.useId();
  const tableId = `fm-table-${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  const fit = useTableFit(
    usesLabelledCells,
    `${props.rows.length}:${columns.map((col) => col.key).join(',')}`,
  );

  const {
    effectiveSortState,
    internalRows,
    effectiveExpandedKeys,
    handleSort,
    toggleRowExpanded,
  } = useFmTableCore(props, canSort);

  const actionLabels = {
    rowActions: a11yLabels.rowActionsLabel,
    moreActions: a11yLabels.moreActions,
  };

  const renderCell = (row: T, col: FmTableColumn<T>): React.ReactNode => (
    <TableCell
      key={col.key}
      row={row}
      col={col}
      usesLabelledCells={usesLabelledCells}
      emptyCellLabel={emptyCellLabel}
      locale={locale}
      actionLabels={actionLabels}
    />
  );

  const renderDataRow = (
    record: Parameters<typeof TableDataRow<T>>[0]['record'],
  ): React.ReactNode => (
    <TableDataRow
      key={record.key}
      record={record}
      rowProps={rowProps}
      rowClassName={rowClassName}
      rowTestId={rowTestId}
      onRowSelect={onRowSelect}
      onRowDoubleClick={onRowDoubleClick}
      rowAriaLabel={rowAriaLabel}
      rowCurrent={rowCurrent}
      rowDataAttributes={rowDataAttributes}
      expandable={expandable}
      expandedKeys={effectiveExpandedKeys}
      tableId={tableId}
      totalColumnCount={totalColumnCount}
      showExpandColumn={showExpandColumn}
      columns={columns}
      rowKey={rowKey}
      a11yLabels={a11yLabels}
      onToggleRow={toggleRowExpanded}
      renderCell={renderCell}
    />
  );

  if (error) {
    return (
      <TableErrorState
        caption={caption}
        tableId={tableId}
        tableClass={tableClass}
        errorShellTestId={errorShellTestId}
        error={error}
        style={style}
      />
    );
  }

  return (
    <TableShell
      hostRef={fit.ref}
      tableClass={cx(
        tableClass,
        fit.cards && FM_TABLE_LABELLED_CLASS,
        fit.narrow && FM_TABLE_NARROW_CLASS,
        fit.pinnedFirst && FM_TABLE_PINNED_CLASS,
      )}
      style={style}
      loading={loading}
      loadingTestId={loadingTestId}
      dataTestId={dataTestId}
      caption={caption}
      tableId={tableId}
      columns={columns}
      showExpandColumn={showExpandColumn}
      totalColumnCount={totalColumnCount}
      usesLabelledCells={usesLabelledCells}
      headerRowTestId={headerRowTestId}
      headTestId={headTestId}
      expandable={expandable}
      canSort={canSort}
      effectiveSortState={effectiveSortState}
      a11yLabels={a11yLabels}
      onSort={handleSort}
      internalRows={internalRows}
      emptyText={emptyText}
      groupHeader={groupHeader}
      groupCountLabel={groupCountLabel}
      renderDataRow={renderDataRow}
      footerRow={footerRow}
      summaryContent={summaryContent}
      footerRowClassName={footerRowClassName}
      footerRowTestId={footerRowTestId}
      renderCell={renderCell}
    />
  );
}

export { FmTable };
export type { FmTableColumn, FmTableProps };
